const DEFAULT_STATS = { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 };
const SAVE_SCOPE_RX = /([+-])\s*(\d+)\s+(?:to\s+)?(all|str|dex|con|int|wis|cha)\s+saves?/i;
const SKILL_BONUS_RX = /([+-])\s*(\d+)\s+(\w+(?:\s+\w+)?)/i;
const MAX_HP_RX = /([+-])\s*(\d+)\s*max\s*hp/i;
const CON_RX = /^\s*([+-])\s*(\d+)\s*(con|constitution)\s*$/i;
const WARDSAVE_RX = /([+-])\s*(\d+)\s*%\s*(?:ws|wardsave|ward\s*save)\b/i;
const AC_RX = /([+-])\s*(\d+)\s*AC\b/i;
const SAVE_EFFECT_SCOPES = new Set(['saves', 'any', 'ability_checks_saves', 'attacks_and_saves']);

const normalizeStats = (stats = {}) => ({ ...DEFAULT_STATS, ...(stats || {}) });
const toArray = (value) => Array.isArray(value) ? value : [];
const formatSigned = (value) => `${value >= 0 ? '+' : ''}${value}`;

export const getAbilityModifier = (score) => Math.floor(((score ?? 10) - 10) / 2);

export const getEquippedCombatBonusesFromInventory = (inventory = []) => {
  const bonuses = [];
  toArray(inventory).forEach((item) => {
    if (!item?.equipped || !Array.isArray(item.combatBonuses)) return;
    item.combatBonuses.forEach((bonus) => bonuses.push({
      ...bonus,
      source: item.name,
      sourceType: 'item',
      sourceMode: 'item-equip',
      itemEntryId: item.id || null,
      itemId: item.itemId || item.id || null,
      itemDefinitionId: item.definitionId || item.itemId || null,
      itemName: item.name || null,
      itemProvenance: item.provenance || null,
    }));
  });
  return bonuses;
};

export const parseRuntimeEffectModifier = (modifier) => {
  if (!modifier || typeof modifier !== 'string') return null;
  const clean = modifier.trim();
  const percentMatch = clean.match(/^([+-]?)(\d+)%/);
  if (percentMatch) {
    const value = parseInt(percentMatch[2], 10);
    return { type: 'percent', value: percentMatch[1] === '-' ? -value : value, display: clean };
  }

  const diceMatch = clean.match(/^([+-]?)(\d*)d(\d+)/i);
  if (diceMatch) {
    const count = parseInt(diceMatch[2], 10) || 1;
    const sides = parseInt(diceMatch[3], 10);
    return {
      type: 'dice',
      dice: `${count}d${sides}`,
      isNegative: diceMatch[1] === '-',
      display: clean,
    };
  }

  const flatMatch = clean.match(/^([+-]?)(\d+)(?:\s|$)/);
  if (flatMatch) {
    const value = parseInt(flatMatch[2], 10);
    return { type: 'flat', value: flatMatch[1] === '-' ? -value : value, display: clean };
  }

  return null;
};

const sumPassiveBonus = (bonuses, scope) => (
  toArray(bonuses).reduce((sum, bonus) => (
    bonus?.type === 'passive' && bonus?.scope === scope ? sum + (bonus.value || 0) : sum
  ), 0)
);

const getPassiveBonusSources = (bonuses, scope) => (
  toArray(bonuses)
    .filter((bonus) => bonus?.type === 'passive' && bonus?.scope === scope && bonus?.value)
    .map((bonus) => ({
      source: bonus.source || bonus.label || 'Item',
      value: bonus.value || 0,
      label: bonus.label || null,
    }))
);

const normalizeScope = (scope) => String(scope || '').trim().toLowerCase();

const parseLegacyAdvantageText = (text) => {
  const match = String(text || '').trim().toLowerCase().match(/^(adv|dis)\s+(.+)$/);
  if (!match) return null;
  return {
    mode: match[1] === 'adv' ? 'advantage' : 'disadvantage',
    target: match[2].trim(),
  };
};

const legacyAdvantageTargetMatchesScope = (target, scope) => {
  const scopeLower = normalizeScope(scope);
  if (!scopeLower) return false;
  const parts = scopeLower.split('_');
  const scopeStat = parts[0];
  const scopeType = parts.slice(1).join('_');

  if (target === 'all checks' && scopeType === 'checks') return true;
  if (target === 'all saves' && scopeType === 'saves') return true;
  if (target === 'attacks' && scopeLower === 'attack') return true;
  if (target === 'attack rolls' && scopeLower === 'attack') return true;

  const targetParts = target.split(/\s+/);
  if (targetParts.length === 1) {
    return targetParts[0] === scopeStat;
  }

  if (targetParts.length >= 2) {
    const targetStat = targetParts[0];
    const targetType = targetParts.slice(1).join(' ');
    if (targetStat === scopeStat && (targetType === scopeType || targetType === scopeType.replace('_', ' '))) return true;
    if (targetStat === scopeStat && targetType === 'checks' && scopeType === 'checks') return true;
  }

  return false;
};

const getRuntimeEffectAdvantageMode = (effect) => {
  if (effect?.grantsAdvantage) return 'advantage';
  if (effect?.grantsDisadvantage) return 'disadvantage';
  if (effect?.type === 'advantage' || effect?.operation === 'advantage') return 'advantage';
  if (effect?.type === 'disadvantage' || effect?.operation === 'disadvantage') return 'disadvantage';

  const modifier = normalizeScope(effect?.modifier);
  if (modifier === 'advantage' || modifier === 'adv') return 'advantage';
  if (modifier === 'disadvantage' || modifier === 'dis') return 'disadvantage';
  return null;
};

const runtimeEffectMatchesScope = (effect, scopeSet) => {
  if (!effect || scopeSet.size === 0) return false;
  const appliesTo = normalizeScope(effect.appliesTo);
  if (!appliesTo) return false;
  if (scopeSet.has(appliesTo)) return true;

  const hasChecksScope = Array.from(scopeSet).some((scope) => scope.endsWith('_checks') || scope === 'all_checks');
  const hasSavesScope = Array.from(scopeSet).some((scope) => scope.endsWith('_saves') || scope === 'all_saves' || scope === 'concentration');
  const hasAttackScope = Array.from(scopeSet).some((scope) => scope === 'attack' || scope === 'attack_rolls' || scope === 'melee_attacks' || scope === 'ranged_attacks');

  if (appliesTo === 'any') return true;
  if (appliesTo === 'saves') return hasSavesScope;
  if (appliesTo === 'attacks') return hasAttackScope;
  if (appliesTo === 'attacks_and_saves') return hasAttackScope || hasSavesScope;
  if (appliesTo === 'ability_checks_saves') return hasChecksScope || hasSavesScope;
  if (appliesTo === 'ability_checks') return hasChecksScope;
  return false;
};

export const getResolvedAdvantageContext = ({
  manualMode = 'normal',
  manualSource = 'Manual toggle',
  equippedCombatBonuses = [],
  activeLegacyEffects = [],
  activeRuntimeEffects = [],
  itemScopes = [],
  supremeItemScopes = [],
  legacyScopes = [],
  runtimeScopes = [],
} = {}) => {
  if (manualMode && manualMode !== 'normal') {
    return {
      mode: manualMode,
      sources: [manualSource],
      advantageSources: manualMode === 'advantage' ? [manualSource] : [],
      disadvantageSources: manualMode === 'disadvantage' ? [manualSource] : [],
      supremeAdvantageSources: manualMode === 'supreme_advantage' ? [manualSource] : [],
    };
  }

  const normalizedItemScopes = new Set(toArray(itemScopes).map(normalizeScope).filter(Boolean));
  const normalizedSupremeItemScopes = new Set(toArray(supremeItemScopes).map(normalizeScope).filter(Boolean));
  const normalizedLegacyScopes = new Set(toArray(legacyScopes).map(normalizeScope).filter(Boolean));
  const normalizedRuntimeScopes = new Set(toArray(runtimeScopes).map(normalizeScope).filter(Boolean));

  const advantageSources = [];
  const disadvantageSources = [];
  const supremeAdvantageSources = [];

  const pushSource = (mode, source) => {
    if (!source) return;
    if (mode === 'advantage') advantageSources.push(source);
    if (mode === 'disadvantage') disadvantageSources.push(source);
    if (mode === 'supreme_advantage') supremeAdvantageSources.push(source);
  };

  toArray(equippedCombatBonuses).forEach((bonus) => {
    const mode = bonus?.type === 'advantage' || bonus?.type === 'disadvantage' || bonus?.type === 'supreme_advantage' ? bonus.type : null;
    if (!mode) return;
    const normalizedBonusScope = normalizeScope(bonus.scope);
    if (!normalizedItemScopes.has(normalizedBonusScope)) return;
    if (mode === 'supreme_advantage' && !normalizedSupremeItemScopes.has(normalizedBonusScope)) return;
    pushSource(mode, bonus.source || bonus.label || 'Equipped item');
  });

  toArray(activeLegacyEffects).forEach((effect) => {
    const parsed = parseLegacyAdvantageText(effect?.text);
    if (!parsed) return;
    const matches = Array.from(normalizedLegacyScopes).some((scope) => legacyAdvantageTargetMatchesScope(parsed.target, scope));
    if (!matches) return;
    pushSource(parsed.mode, effect?.text || effect?.source || 'Effect');
  });

  toArray(activeRuntimeEffects).forEach((effect) => {
    const mode = getRuntimeEffectAdvantageMode(effect);
    if (!mode) return;
    if (!runtimeEffectMatchesScope(effect, normalizedRuntimeScopes)) return;
    pushSource(mode, effect?.source || effect?.name || effect?.casterName || 'Effect');
  });

  const hasAdvantage = advantageSources.length > 0;
  const hasDisadvantage = disadvantageSources.length > 0;
  const hasSupremeAdvantage = supremeAdvantageSources.length > 0;
  const hasAnyAdvantage = hasAdvantage || hasSupremeAdvantage;
  const mode = hasSupremeAdvantage && !hasDisadvantage
    ? 'supreme_advantage'
    : hasAdvantage && !hasDisadvantage
      ? 'advantage'
      : hasDisadvantage && !hasAnyAdvantage
        ? 'disadvantage'
        : 'normal';

  return {
    mode,
    sources: mode === 'supreme_advantage'
      ? [...supremeAdvantageSources, ...advantageSources]
      : mode === 'advantage'
      ? advantageSources
      : mode === 'disadvantage'
        ? disadvantageSources
        : [...supremeAdvantageSources, ...advantageSources, ...disadvantageSources],
    advantageSources,
    disadvantageSources,
    supremeAdvantageSources,
  };
};

export const getStatCheckAdvantageConfig = (stat) => {
  const statKey = normalizeScope(stat);
  return {
    itemScopes: [`${statKey}_checks`, 'all_checks'],
    legacyScopes: [`${statKey}_checks`, 'all_checks'],
  };
};

const EXACT_SUPREME_SKILL_ITEM_SCOPES = new Set(['stealth', 'perception']);
const BROAD_SUPREME_SKILL_ITEM_SCOPES = new Set(['str_skills', 'dex_skills', 'int_skills', 'wis_skills', 'cha_skills']);
const EXACT_SUPREME_SAVE_ITEM_SCOPES = new Set([
  'str_saves',
  'dex_saves',
  'con_saves',
  'int_saves',
  'wis_saves',
  'cha_saves',
]);

export const getSkillAdvantageConfig = (skill, stat) => {
  const statKey = normalizeScope(stat);
  const skillKey = String(skill || '').trim().toLowerCase();
  const config = {
    itemScopes: ['all_checks', skillKey, `${statKey}_skills`],
    legacyScopes: [`${statKey}_checks`],
  };
  const supremeItemScopes = ['all_checks'];
  const statSkillScope = `${statKey}_skills`;
  if (BROAD_SUPREME_SKILL_ITEM_SCOPES.has(statSkillScope)) {
    supremeItemScopes.push(statSkillScope);
  }
  if (EXACT_SUPREME_SKILL_ITEM_SCOPES.has(skillKey)) {
    supremeItemScopes.push(skillKey);
  }
  config.supremeItemScopes = supremeItemScopes;
  return {
    ...config,
  };
};

export const getSaveAdvantageConfig = (stat) => {
  const statKey = normalizeScope(stat);
  const exactSaveScope = `${statKey}_saves`;
  const config = {
    itemScopes: [exactSaveScope, 'all_saves'],
    legacyScopes: [exactSaveScope, 'all_saves'],
  };
  if (EXACT_SUPREME_SAVE_ITEM_SCOPES.has(exactSaveScope)) {
    config.supremeItemScopes = [exactSaveScope, 'all_saves'];
  }
  return config;
};

export const getCombatAdvantageScopeConfig = (scope) => {
  const scopeKey = normalizeScope(scope);
  if (!scopeKey) return { itemScopes: [], legacyScopes: [], runtimeScopes: [] };

  if (scopeKey === 'attack') {
    return {
      itemScopes: ['attack', 'attack_rolls'],
      supremeItemScopes: ['attack', 'attack_rolls'],
      legacyScopes: ['attack'],
      runtimeScopes: ['attack', 'attack_rolls'],
    };
  }

  if (scopeKey === 'melee_attacks' || scopeKey === 'ranged_attacks') {
    return {
      itemScopes: [scopeKey, 'attack', 'attack_rolls'],
      supremeItemScopes: [scopeKey, 'attack', 'attack_rolls'],
      legacyScopes: [scopeKey, 'attack'],
      runtimeScopes: [scopeKey, 'attack', 'attack_rolls'],
    };
  }

  if (scopeKey === 'concentration') {
    return {
      itemScopes: ['concentration', 'con_saves', 'all_saves'],
      supremeItemScopes: ['concentration', 'all_saves'],
      legacyScopes: ['concentration', 'con_saves', 'all_saves'],
      runtimeScopes: ['concentration', 'con_saves', 'all_saves'],
    };
  }

  if (scopeKey === 'initiative') {
    return {
      itemScopes: ['initiative'],
      supremeItemScopes: ['initiative'],
      legacyScopes: ['initiative'],
      runtimeScopes: ['initiative'],
    };
  }

  if (scopeKey.endsWith('_saves')) {
    const config = {
      itemScopes: [scopeKey, 'all_saves'],
      legacyScopes: [scopeKey, 'all_saves'],
      runtimeScopes: [scopeKey, 'all_saves'],
    };
    if (scopeKey === 'death_saves' || EXACT_SUPREME_SAVE_ITEM_SCOPES.has(scopeKey)) {
      config.supremeItemScopes = [scopeKey, 'all_saves'];
    }
    return config;
  }

  return {
    itemScopes: [scopeKey],
    legacyScopes: [scopeKey],
    runtimeScopes: [scopeKey],
  };
};

export const getResolvedAttackBonusContext = ({
  equippedCombatBonuses = [],
  itemScopes = [],
  scope = '',
} = {}) => {
  const normalizedItemScopes = new Set(toArray(itemScopes).map(normalizeScope).filter(Boolean));
  if (normalizedItemScopes.size === 0 && scope) normalizedItemScopes.add(normalizeScope(scope));

  const itemSources = [];
  let itemBonus = 0;

  toArray(equippedCombatBonuses).forEach((bonus) => {
    if (bonus?.type !== 'passive') return;
    const normalizedBonusScope = normalizeScope(bonus.scope);
    if (!normalizedItemScopes.has(normalizedBonusScope)) return;
    const value = Number(bonus.value) || 0;
    if (!value) return;
    itemBonus += value;
    itemSources.push({
      source: bonus.source || bonus.label || 'Item',
      value,
      label: bonus.label || null,
      scope: normalizedBonusScope,
    });
  });

  return {
    scope: normalizeScope(scope),
    itemBonus,
    itemSources,
    totalBonus: itemBonus,
  };
};

export const getInitiativeAdvantageConfig = () => ({
  itemScopes: ['initiative'],
  supremeItemScopes: ['initiative'],
  legacyScopes: ['initiative'],
});

export const getResolvedSkillContext = ({
  skill,
  stat,
  char = null,
  effectiveStats = null,
  statModifiers = [],
  activeLegacyEffects = [],
} = {}) => {
  const stats = normalizeStats(effectiveStats || char?.baseStats);
  const abilityMod = getAbilityModifier(stats[stat]);
  const proficiencyBonus = typeof char?.level === 'number' ? Math.ceil(char.level / 4) + 1 : 2;
  const isProficient = toArray(char?.skillProficiencies).includes(skill);
  const isExpertise = toArray(char?.skillExpertise).includes(skill);
  const miscMod = (char?.skillMiscMods || {})[skill] || 0;
  const equippedItemNames = new Set(
    toArray(char?.inventory)
      .filter((item) => item?.equipped)
      .map((item) => item.name)
      .filter(Boolean)
  );

  const skillBonusSources = [];
  toArray(activeLegacyEffects).forEach((effect) => {
    const match = String(effect?.text || '').match(SKILL_BONUS_RX);
    if (!match) return;
    const value = (match[1] === '+' ? 1 : -1) * parseInt(match[2], 10);
    const skillName = match[3].trim();
    if (skillName.toLowerCase() !== String(skill || '').toLowerCase()) return;

    const source = effect?.source || 'Manual';
    let note = effect?.note || (effect?.permanence === 'permanent' ? 'Permanent' : 'Temporary');
    if (equippedItemNames.has(source)) note = 'Equipped';
    else if (/^diet:/i.test(source) || /diet/i.test(source)) note = 'Diet Reward';

    skillBonusSources.push({
      value,
      source,
      note,
      isPermanent: effect?.permanence === 'permanent',
    });
  });

  const totalSkillBonus = skillBonusSources.reduce((sum, entry) => sum + entry.value, 0);
  const proficiencyContribution = isExpertise ? proficiencyBonus * 2 : isProficient ? proficiencyBonus : 0;

  return {
    skill,
    stat,
    statScore: stats[stat],
    abilityMod,
    proficiencyBonus,
    proficiencyContribution,
    isProficient,
    isExpertise,
    miscMod,
    skillBonusSources,
    statModifierEffects: toArray(statModifiers),
    totalBonus: abilityMod + proficiencyContribution + miscMod + totalSkillBonus,
  };
};

export const getResolvedSkillBreakdownParts = (context) => {
  if (!context) return [];
  const parts = [{
    type: 'stat',
    value: context.abilityMod,
    source: `${String(context.stat || '').toUpperCase()} mod`,
    details: context.statModifierEffects.length > 0
      ? context.statModifierEffects.map((entry) => `${entry.effect} (${entry.source})`).join(', ')
      : `Score: ${context.statScore ?? 10}`,
  }];

  if (context.isExpertise) parts.push({ type: 'prof', value: context.proficiencyContribution, source: 'Expertise' });
  else if (context.isProficient) parts.push({ type: 'prof', value: context.proficiencyContribution, source: 'Proficiency' });

  if (context.miscMod !== 0) parts.push({ type: 'misc', value: context.miscMod, source: 'Misc mod' });

  context.skillBonusSources.forEach((entry) => {
    parts.push({
      type: entry.note === 'Equipped' ? 'equip' : 'buff',
      value: entry.value,
      source: entry.source,
      details: entry.note || null,
    });
  });

  return parts;
};

export const getResolvedSkillTooltipLines = (context) => {
  if (!context) return [];
  const lines = [`${String(context.stat || '').toUpperCase()} mod: ${formatSigned(context.abilityMod)}`];
  if (context.statModifierEffects.length > 0) {
    context.statModifierEffects.forEach((entry) => {
      lines.push(`  ${entry.effect} (${entry.source}) - ${entry.note}`);
    });
  }
  if (context.isExpertise) lines.push(`Expertise: +${context.proficiencyContribution}`);
  else if (context.isProficient) lines.push(`Proficiency: +${context.proficiencyContribution}`);
  if (context.miscMod !== 0) lines.push(`Misc mod: ${formatSigned(context.miscMod)}`);
  if (context.skillBonusSources.length > 0) {
    lines.push('');
    lines.push('Skill Buffs:');
    context.skillBonusSources.forEach((entry) => {
      lines.push(`  ${formatSigned(entry.value)} (${entry.source}) - ${entry.note}`);
    });
  }
  lines.push('');
  lines.push(`Total: ${formatSigned(context.totalBonus)}`);
  return lines;
};

export const getResolvedPassiveSkillContext = ({
  skillContext = null,
  equippedCombatBonuses = [],
} = {}) => {
  const passiveScope = `passive_${String(skillContext?.skill || '').toLowerCase().replace(/\s+/g, '_')}`;
  const passiveItemBonus = sumPassiveBonus(equippedCombatBonuses, passiveScope);
  return {
    skill: skillContext?.skill || '',
    passiveScope,
    passiveItemBonus,
    passiveItemSources: getPassiveBonusSources(equippedCombatBonuses, passiveScope),
    totalScore: 10 + (skillContext?.totalBonus || 0) + passiveItemBonus,
  };
};

export const getResolvedSpellContext = ({
  char,
  effectiveStats = null,
  equippedCombatBonuses = [],
} = {}) => {
  const stats = normalizeStats(effectiveStats || char?.baseStats);
  const spellStat = char?.spellcastingStat || 'wis';
  const statMod = getAbilityModifier(stats[spellStat]);
  const proficiencyBonus = typeof char?.level === 'number' ? Math.ceil(char.level / 4) + 1 : 2;
  const spellModifier = statMod + proficiencyBonus;
  const spellAttackItemBonus = sumPassiveBonus(equippedCombatBonuses, 'spell_attack');
  const spellSaveDcItemBonus = sumPassiveBonus(equippedCombatBonuses, 'spell_save_dc');

  return {
    spellStat,
    statScore: stats[spellStat],
    statMod,
    proficiencyBonus,
    spellModifier,
    spellAttackBonus: spellModifier + spellAttackItemBonus,
    spellSaveDC: 8 + spellModifier + spellSaveDcItemBonus,
    spellAttackItemBonus,
    spellSaveDcItemBonus,
    parts: [
      { name: spellStat.toUpperCase(), value: statMod },
      { name: 'Proficiency', value: proficiencyBonus },
      ...(spellAttackItemBonus ? [{ name: 'Spell Attack', value: spellAttackItemBonus }] : []),
      ...(spellSaveDcItemBonus ? [{ name: 'Spell Save DC', value: spellSaveDcItemBonus }] : []),
    ],
  };
};

export const getResolvedSaveContext = ({
  stat,
  char = null,
  effectiveStats = null,
  equippedCombatBonuses = [],
  activeLegacyEffects = [],
  activeRuntimeEffects = [],
  enemySaveBonus = null,
} = {}) => {
  if (Number.isFinite(enemySaveBonus)) {
    return {
      stat,
      statScore: null,
      abilityMod: 0,
      proficiencyBonus: 0,
      itemBonus: 0,
      textBonus: 0,
      runtimeFlatBonus: 0,
      totalBonus: enemySaveBonus,
      diceBonuses: [],
      itemSources: [],
      textSources: [],
      runtimeFlatSources: [],
      isEnemy: true,
    };
  }

  const stats = normalizeStats(effectiveStats || char?.baseStats);
  const abilityMod = getAbilityModifier(stats[stat]);
  const proficiencyBonus = (char?.savingThrowProficiencies || []).includes(stat)
    ? (typeof char?.level === 'number' ? Math.ceil(char.level / 4) + 1 : 2)
    : 0;

  let itemBonus = 0;
  const itemSources = [];
  toArray(equippedCombatBonuses).forEach((bonus) => {
    if (bonus?.type === 'save_bonus' && (bonus.scope === `${stat}_saves` || bonus.scope === 'all_saves')) {
      itemBonus += bonus.value || 0;
      itemSources.push({ source: bonus.source || bonus.label || 'Item', value: bonus.value || 0, label: bonus.label || null });
    }
  });

  let textBonus = 0;
  const textSources = [];
  const legacyFlatRuntimeDedupKeys = new Set();
  toArray(activeLegacyEffects).forEach((effect) => {
    const match = String(effect?.text || '').match(SAVE_SCOPE_RX);
    if (!match) return;
    const scope = match[3].toLowerCase();
    if (scope !== 'all' && scope !== stat) return;
    const value = (match[1] === '+' ? 1 : -1) * parseInt(match[2], 10);
    textBonus += value;
    const source = effect?.source || 'Effect';
    textSources.push({ source, value, label: effect?.text || '' });
    legacyFlatRuntimeDedupKeys.add(`${value}:${source}`);
  });

  let runtimeFlatBonus = 0;
  const runtimeFlatSources = [];
  const diceBonuses = [];
  toArray(activeRuntimeEffects).forEach((effect) => {
    if (!SAVE_EFFECT_SCOPES.has(effect?.appliesTo)) return;
    const parsed = effect?.parsed || parseRuntimeEffectModifier(effect?.modifier);
    if (!parsed) return;
    const runtimeSource = effect?.source || effect?.name || effect?.casterName || 'Effect';
    if (parsed.type === 'flat' && parsed.value) {
      if (legacyFlatRuntimeDedupKeys.has(`${parsed.value}:${runtimeSource}`)) return;
      runtimeFlatBonus += parsed.value;
      runtimeFlatSources.push({ source: runtimeSource, value: parsed.value, label: effect?.modifier || '' });
    } else if (parsed.type === 'dice' && parsed.dice && !parsed.isNegative) {
      diceBonuses.push({ source: runtimeSource, dice: parsed.dice, label: effect?.modifier || parsed.dice });
    }
  });

  return {
    stat,
    statScore: stats[stat],
    abilityMod,
    proficiencyBonus,
    itemBonus,
    textBonus,
    runtimeFlatBonus,
    totalBonus: abilityMod + proficiencyBonus + itemBonus + textBonus + runtimeFlatBonus,
    diceBonuses,
    itemSources,
    textSources,
    runtimeFlatSources,
    isEnemy: false,
  };
};

export const getResolvedArmorClassContext = ({
  char = null,
  effectiveStats = null,
  equippedCombatBonuses = [],
  activeLegacyEffects = [],
  activeRuntimeEffects = [],
  armorTypes = {},
  customArmorTypes = {},
} = {}) => {
  const stats = normalizeStats(effectiveStats || char?.baseStats);
  const equippedCustomArmor = toArray(char?.equipment)
    .find((entry) => entry?.id === char?.equippedArmor && entry?.type === 'armor') || null;
  const customArmorTypeData = equippedCustomArmor ? customArmorTypes?.[equippedCustomArmor.subtype] : null;
  const fallbackArmor = armorTypes?.[char?.armorType] || armorTypes?.none || { name: 'Unarmored', baseAC: 10 };
  const currentArmor = equippedCustomArmor
    ? {
        name: equippedCustomArmor.name || customArmorTypeData?.name || 'Custom Armor',
        description: customArmorTypeData?.description || '',
        baseAC: customArmorTypeData?.baseAC ?? equippedCustomArmor.baseAC ?? 10,
        icon: customArmorTypeData?.icon || '🛡️',
      }
    : fallbackArmor;

  const baseArmorClass = currentArmor?.baseAC ?? 10;
  const dexModifier = getAbilityModifier(stats.dex);
  const hasOverride = typeof char?.armorClass === 'number' && char.armorClass !== 10;
  const overrideArmorClass = hasOverride ? char.armorClass : null;
  const baseTotalArmorClass = hasOverride ? char.armorClass : baseArmorClass + dexModifier;

  let legacyBonus = 0;
  const legacySources = [];
  const legacyDedupKeys = new Set();
  toArray(activeLegacyEffects).forEach((effect) => {
    const match = String(effect?.text || '').match(AC_RX);
    if (!match) return;
    const value = (match[1] === '+' ? 1 : -1) * parseInt(match[2], 10);
    legacyBonus += value;
    const source = effect?.source || 'Effect';
    legacySources.push({ source, value, label: effect?.text || '' });
    legacyDedupKeys.add(`${value}:${source}`);
  });

  let itemBonus = 0;
  const itemSources = [];
  const flatDedupKeys = new Set(legacyDedupKeys);
  toArray(equippedCombatBonuses).forEach((bonus) => {
    if (bonus?.type !== 'passive' || normalizeScope(bonus?.scope) !== 'ac') return;
    const value = Number(bonus?.value) || 0;
    if (!value) return;
    const source = bonus?.source || bonus?.itemName || bonus?.label || 'Item';
    const dedupKey = `${value}:${source}`;
    if (flatDedupKeys.has(dedupKey)) return;
    flatDedupKeys.add(dedupKey);
    itemBonus += value;
    itemSources.push({
      source,
      value,
      label: bonus?.label || null,
      itemEntryId: bonus?.itemEntryId || null,
      itemId: bonus?.itemId || null,
      itemDefinitionId: bonus?.itemDefinitionId || null,
      itemName: bonus?.itemName || source,
      itemProvenance: bonus?.itemProvenance || null,
    });
  });

  let runtimeBonus = 0;
  const runtimeSources = [];
  toArray(activeRuntimeEffects).forEach((effect) => {
    if (!(effect?.appliesTo === 'ac' || String(effect?.modifier || '').toLowerCase().includes('ac'))) return;
    const parsed = effect?.parsed || parseRuntimeEffectModifier(effect?.modifier);
    if (!parsed || parsed.type !== 'flat' || !parsed.value) return;
    const source = effect?.source || effect?.name || effect?.casterName || 'Effect';
    if (flatDedupKeys.has(`${parsed.value}:${source}`)) return;
    runtimeBonus += parsed.value;
    runtimeSources.push({ source, value: parsed.value, label: effect?.modifier || '' });
  });

  return {
    currentArmor,
    baseArmorClass,
    dexModifier,
    hasOverride,
    overrideArmorClass,
    baseTotalArmorClass,
    itemBonus,
    legacyBonus,
    runtimeBonus,
    totalBonus: itemBonus + legacyBonus + runtimeBonus,
    totalArmorClass: baseTotalArmorClass + itemBonus + legacyBonus + runtimeBonus,
    itemSources,
    legacySources,
    runtimeSources,
  };
};

export const formatResolvedSaveBreakdown = (context) => {
  if (!context) return '';
  if (context.isEnemy) {
    return `Enemy save: ${formatSigned(context.totalBonus)}`;
  }

  const parts = [
    `${context.stat.toUpperCase()}: ${context.statScore ?? 10} (${formatSigned(context.abilityMod)})`,
  ];
  if (context.proficiencyBonus) parts.push(`Prof(${formatSigned(context.proficiencyBonus)})`);
  context.itemSources.forEach((entry) => parts.push(`${entry.source} ${formatSigned(entry.value)}`));
  context.textSources.forEach((entry) => parts.push(`${entry.label || entry.source} ${formatSigned(entry.value)}`));
  context.runtimeFlatSources.forEach((entry) => parts.push(`${entry.source} ${formatSigned(entry.value)}`));
  context.diceBonuses.forEach((entry) => parts.push(`${entry.dice} (${entry.source})`));
  return parts.join(' + ');
};

export const getResolvedMaxHpContext = ({
  char,
  activeLegacyEffects = [],
} = {}) => {
  let effectiveMaxHp = char?.maxHp || 1;
  let conDelta = 0;

  toArray(activeLegacyEffects).forEach((effect) => {
    const text = String(effect?.text || '');
    const maxHpMatch = text.match(MAX_HP_RX);
    if (maxHpMatch) {
      const value = (maxHpMatch[1] === '+' ? 1 : -1) * parseInt(maxHpMatch[2], 10);
      effectiveMaxHp += value;
      return;
    }

    if (/\bsaves?\b/i.test(text)) return;
    const conMatch = text.match(CON_RX);
    if (conMatch) {
      const value = (conMatch[1] === '+' ? 1 : -1) * parseInt(conMatch[2], 10);
      conDelta += value;
    }
  });

  if (conDelta !== 0) {
    const baseCon = char?.baseStats?.con ?? 10;
    const newCon = baseCon + conDelta;
    const modDelta = getAbilityModifier(newCon) - getAbilityModifier(baseCon);
    effectiveMaxHp += modDelta * (char?.level || 1);
  }

  return {
    effectiveMaxHp: Math.max(1, effectiveMaxHp),
    conDelta,
  };
};

export const getResolvedWardsaveContext = ({
  activeLegacyEffects = [],
} = {}) => {
  let bestPos = 0;
  let bestPosSource = null;
  let neg = 0;

  toArray(activeLegacyEffects).forEach((effect) => {
    const match = String(effect?.text || '').match(WARDSAVE_RX);
    if (!match) return;
    const value = parseInt(match[2], 10) || 0;
    if (match[1] === '+' && value > bestPos) {
      bestPos = value;
      bestPosSource = effect?.source || 'Manual';
    } else if (match[1] === '-') {
      neg += value;
    }
  });

  return {
    total: Math.max(0, Math.min(100, bestPos - neg)),
    bestPos,
    bestPosSource,
    neg,
  };
};

export const getResolvedWardsaveReduction = ({
  currentDamage = 0,
  activeLegacyEffects = [],
} = {}) => {
  const context = getResolvedWardsaveContext({ activeLegacyEffects });
  const reduction = context.total > 0 && currentDamage > 0
    ? Math.floor(currentDamage * (context.total / 100))
    : 0;
  return {
    ...context,
    reduction,
    sourceLabel: context.bestPosSource ? `Wardsave (${context.bestPosSource})` : 'Wardsave',
  };
};
